<template>
  <ui-overlay-loader :loading="registryChecks.isLoading">
    <ui-heading tag="h3" no-top-margin>
      {{ registryChecks.label }}
      <ui-button
        @click="reprocess"
        variant="primary"
        size="sm"
        class="reprocess-button"
        :disabled="registryChecks.isLoading"
      >
        {{ $t("checks_reprocess") }}
        <icon name="refresh" class="refresh-icon" />
      </ui-button>
    </ui-heading>
    <ul class="list">
      <li
        v-for="item in registryChecks.items"
        :key="item.key"
        :class="['item', { 'missing-data': item.missingData }]"
      >
        <icon name="check-circle" v-if="item.success" class="icon success" />
        <icon name="alert-circle" v-else-if="item.fail" class="icon fail" />
        <icon name="alert-circle" v-else class="icon unavailable" />
        <checks-item :item="item" />
        <template v-if="item.screeningLink && screeningRoute">
          <router-link :to="screeningRoute" class="info-link" target="_blank">
            {{ $t("checks_rules_view_match") }}
            <icon name="open-in-new" class="link-icon" />
          </router-link>
        </template>
      </li>
    </ul>
  </ui-overlay-loader>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { actions, getters } from "@/store";
import { CHECK_REASON, CHECK_STATUS, ROUTE_NAMES } from "@/router/constants";
import { SCREENING_QUERY_PARAMS, THRESHOLD_VALUE } from "@/constants/screening";
import { IDENTIFICATION_REGISTRY_STATUS } from "@/constants";
import { getReasonsLabels } from "@/utils/checks";
import ChecksItem from "./ChecksItem.vue";

export default {
  components: { ChecksItem },
  computed: {
    ...mapGetters([getters.currentIdentification, getters.currentDocumentData]),
    screeningRoute() {
      if (!this.currentDocumentData) return null;
      const { firstName, lastName, dateOfBirth } = this.currentDocumentData;
      if (!firstName || !lastName || !dateOfBirth) {
        return null;
      }

      const { NAME, BIRTH_DATE, THRESHOLD } = SCREENING_QUERY_PARAMS;
      const query = {
        [NAME]: `${firstName} ${lastName}`,
        [BIRTH_DATE]: dateOfBirth,
        [THRESHOLD]: THRESHOLD_VALUE
      };

      return {
        name: ROUTE_NAMES.SANCTION_SEARCH,
        query
      };
    },
    registries() {
      return this.currentIdentification?.data?.registries;
    },
    registryChecks() {
      return {
        label: this.$t("checks_registries_heading"),
        isLoading:
          this.registries?.status ===
          IDENTIFICATION_REGISTRY_STATUS.REPROCESSING,
        items: this.registries?.data
          ?.filter(reg => reg.name !== "AML_SCREENING")
          .map(reg => ({
            key: reg.name,
            label: this.$tIf({
              key: `checks_registries.${reg.name}_${reg.status}`
            }),
            reasonLabels: getReasonsLabels(
              reg.reasons,
              "checks_registries_reason",
              this.$tIf
            ),
            screeningLink:
              reg.name === "SANCTIONS_SCREENING" &&
              reg.status === CHECK_STATUS.FAIL,
            missingData: !!reg.reasons?.includes(
              CHECK_REASON.MISSING_REQUIRED_DOCUMENT_DATA
            ),
            success: reg.status === CHECK_STATUS.SUCCESS,
            unavailable: reg.status === CHECK_STATUS.UNAVAILABLE,
            fail: reg.status === CHECK_STATUS.FAIL,
            statusText: this.getStatusText(reg.status)
          }))
      };
    }
  },
  methods: {
    ...mapActions([actions.identificationRegistryReprocess]),
    reprocess() {
      this.identificationRegistryReprocess({
        identificationId: this.currentIdentification?.identificationId
      });
    },
    getStatusText(status) {
      switch (status) {
        case CHECK_STATUS.SUCCESS:
          return this.$t("check_status.success");
        case CHECK_STATUS.FAIL:
          return this.$t("check_status.fail");
        case CHECK_STATUS.UNAVAILABLE:
        default:
          return this.$t("check_status.unavailable");
      }
    }
  }
};
</script>

<style scoped lang="scss">
.list {
  padding: 0;
  margin: 0;
  font-size: 1rem;
  list-style: none;
}
.item {
  position: relative;
  padding-left: 1.5rem;

  & + & {
    margin-top: 0.5rem;
  }

  &.missing-data {
    color: var(--text-color-dimmed);

    .icon {
      fill: currentColor;
    }
  }
}
.icon {
  position: absolute;
  left: 0;
  width: 1.2rem;
  height: 1.2rem;
  &.success {
    fill: var(--text-success);
  }
  &.unavailable {
    fill: var(--text-warning);
  }
  &.fail {
    fill: var(--text-danger);
  }
}
.info-link {
  display: inline-block;
}
.link-icon {
  display: inline-block;
  width: 1em;
  height: 1em;
  vertical-align: bottom;
}
.reprocess-button.reprocess-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0 0 0.5em;
  vertical-align: bottom;
}
.refresh-icon {
  width: 1.5rem;
}
</style>
